import { EventQueuesBundle, EventQueueWireDto } from '@mark43/cad-api';
import { createContext, useContext, useMemo, useRef } from 'react';

type ContextType = {
    eventQueueViewsRef: React.MutableRefObject<Record<number, EventQueueWireDto>>;
    getListeners: (() => EventQueueViewsListener[]) | undefined;
    addListener: ((listener: EventQueueViewsListener) => void) | undefined;
    removeListener: ((listener: EventQueueViewsListener) => void) | undefined;
};

const EventQueueViewsContext = createContext<ContextType>({
    eventQueueViewsRef: { current: {} },
    getListeners: undefined,
    addListener: undefined,
    removeListener: undefined,
});

export type EventQueueViewsListener = {
    onBootstrap: () => void;
    onUpdate: (eventQueuesBundle: EventQueuesBundle) => void;
};

export function EventQueueViewsProvider(props: { children: React.ReactNode }) {
    const eventQueueViewsRef = useRef<ContextType['eventQueueViewsRef']['current']>({});
    const listenersRef = useRef<EventQueueViewsListener[]>([]);
    const contextValue = useMemo(
        () => ({
            eventQueueViewsRef,
            getListeners() {
                return listenersRef.current;
            },
            addListener: (listener: EventQueueViewsListener) => {
                if (!listenersRef.current.includes(listener)) listenersRef.current.push(listener);
                listener.onBootstrap();
            },
            removeListener: (listener: EventQueueViewsListener) => {
                const index = listenersRef.current.indexOf(listener);
                if (index !== -1) {
                    listenersRef.current.splice(index, 1);
                }
            },
        }),
        []
    );
    return (
        <EventQueueViewsContext.Provider value={contextValue}>
            {props.children}
        </EventQueueViewsContext.Provider>
    );
}

export function useEventQueueViews(): ContextType {
    return useContext(EventQueueViewsContext);
}
